import Image from 'next/image';

import {
  BodySmallRegularBold,
  CaptionRegular
} from '@components/atoms/typography';
import { BLANK_SHIRT_PATH } from '@lib/utils';

type EmptySlotProps = {
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  onCancel?: () => void;
  onPress?: () => void;
};

export const EmptySlot = ({ position, onCancel, onPress }: EmptySlotProps) => (
  <div
    role={onPress ? 'button' : undefined}
    tabIndex={onPress ? 0 : undefined}
    onClick={onPress}
    onKeyDown={
      onPress
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onPress();
            }
          }
        : undefined
    }
    className={`flex flex-col items-center w-full ${
      onPress ? 'cursor-pointer' : ''
    }`}
    data-cy="empty-slot"
    data-cy-position={position}
  >
    <div className="relative">
      <div className="w-[42px] h-[30px] md:w-[64px] md:h-[52px] relative opacity-60">
        <Image
          src={BLANK_SHIRT_PATH}
          alt="Empty slot"
          fill
          sizes="(min-width: 768px) 64px, 42px"
          style={{ objectFit: 'contain' }}
          unoptimized
        />
      </div>
      {onCancel && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onCancel();
          }}
          aria-label="Cancel transfer"
          data-cy="empty-slot-cancel"
          className="absolute -top-1 -right-1 flex justify-center items-center rounded-full w-5 h-5 bg-danger-600 text-white"
        >
          <span className="text-xs leading-none">×</span>
        </button>
      )}
    </div>
    <div className="w-full bg-white border border-dashed border-neutral-400 px-1 pt-1 pb-1 text-center -mt-2 relative">
      <CaptionRegular className="text-neutral-600 block">Add</CaptionRegular>
      <BodySmallRegularBold>{position}</BodySmallRegularBold>
    </div>
    {/* Empty spacer that keeps an unfilled slot the same height as a filled
        player card so pitch rows stay aligned; intentionally blank rather
        than showing placeholder dashes that read as missing data. */}
    <div
      className="w-full flex flex-col items-center justify-center px-px bg-neutral-100 rounded-b-lg border-l border-r border-b border-dashed border-neutral-300"
      aria-hidden
    >
      <span className="text-xs md:text-sm font-semibold pt-1 leading-[14px]">
        &nbsp;
      </span>
      <span className="text-[8px] md:text-[10px] pb-1 leading-[12px]">
        &nbsp;
      </span>
    </div>
  </div>
);
